import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import mediaService from '../../services/mediaService';
import Header from '../../components/ui/Header';
import ArticleCard from '../homepage/components/ArticleCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { toArabicDigits } from '../../utils/numbers';

const UserProfile = () => {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated, updateProfile, profileLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('published');

  // Articles state
  const [publishedArticles, setPublishedArticles] = useState([]);
  const [bookmarkArticles, setBookmarkArticles] = useState([]);
  const [publishedLoading, setPublishedLoading] = useState(true);
  const [bookmarksLoading, setBookmarksLoading] = useState(false);
  const [publishedError, setPublishedError] = useState(null);
  const [bookmarksError, setBookmarksError] = useState(null);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());

  // Edit profile state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ fullName: '', bio: '', title: '' });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);

  const bookmarksFetched = useRef(false);

  // Helper to get profile field with camelCase/snake_case fallback
  const getField = useCallback((camel, snake) => {
    return userProfile?.[camel] ?? userProfile?.[snake];
  }, [userProfile]);

  // Map API article data to the shape ArticleCard expects
  const mapArticle = useCallback((article) => ({
    id: article.id,
    title: article.title,
    excerpt: article.excerpt,
    image: article.image || article.coverImage,
    imageAlt: article.imageAlt || article.coverImageAlt || article.title,
    author: article.author || getField('fullName', 'full_name') || 'الكاتب',
    authorId: article.authorId,
    authorImage: article.authorImage || getField('avatarUrl', 'avatar_url') || '',
    authorImageAlt: 'صورة شخصية للكاتب',
    circle: article.circle,
    readTime: article.readTime,
    claps: article.claps || 0,
    publishedDate: article.publishedDate || '',
    type: article.type || 'Article',
  }), [getField]);

  // Fetch published articles on mount
  useEffect(() => {
    const fetchPublished = async () => {
      setPublishedLoading(true);
      setPublishedError(null);
      try {
        const { data } = await articlesService.getMyPublished();
        setPublishedArticles(Array.isArray(data) ? data : []);
      } catch {
        setPublishedError('تعذّر تحميل المنشورات. يرجى المحاولة لاحقاً.');
      } finally {
        setPublishedLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPublished();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab !== 'bookmarks' || bookmarksFetched.current) return;

    const fetchBookmarks = async () => {
      setBookmarksLoading(true);
      setBookmarksError(null);
      try {
        const { data } = await articlesService.getMyBookmarks();
        setBookmarkArticles(Array.isArray(data) ? data : []);
      } catch {
        setBookmarksError('تعذّر تحميل المحفوظات. يرجى المحاولة لاحقاً.');
      } finally {
        setBookmarksLoading(false);
        bookmarksFetched.current = true;
      }
    };

    if (isAuthenticated) {
      fetchBookmarks();
    }
  }, [activeTab, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchBookmarkIds = async () => {
      try {
        const { data } = await articlesService.getMyBookmarkIds();
        setBookmarkedIds(new Set(data));
      } catch (err) {
        console.error('Failed to fetch bookmark IDs:', err);
      }
    };
    fetchBookmarkIds();
  }, [isAuthenticated]);

  const handleBookmarkToggle = async (articleId) => {
    if (!isAuthenticated) return;
    try {
      const wasBookmarked = bookmarkedIds.has(articleId);
      if (wasBookmarked) {
        await engagementService.removeBookmark(articleId);
        setBookmarkedIds(prev => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
        if (activeTab === 'bookmarks') {
          setBookmarkArticles(prev => prev.filter(a => a.id !== articleId));
        }
      } else {
        await engagementService.bookmarkArticle(articleId);
        setBookmarkedIds(prev => new Set(prev).add(articleId));
      }
    } catch (err) {
      console.error('Bookmark toggle failed:', err);
    }
  };

  // Profile stats from userProfile
  const articleCount = userProfile?.articleCount ?? 0;
  const totalClaps = userProfile?.totalClaps ?? 0;
  const followerCount = userProfile?.followerCount ?? 0;
  const circlesCount = userProfile?.selectedCircles?.length ?? 0;

  // Derive display name and avatar with fallbacks
  const displayName = getField('fullName', 'full_name') || 'اسم المستخدم';
  const avatarUrl = getField('avatarUrl', 'avatar_url');

  // Edit profile handlers
  const handleStartEdit = () => {
    setEditForm({
      fullName: getField('fullName', 'full_name') || '',
      bio: userProfile?.bio || '',
      title: userProfile?.title || '',
    });
    setEditError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    setEditSaving(true);
    setEditError(null);
    try {
      const { error } = await updateProfile({
        fullName: editForm.fullName,
        bio: editForm.bio,
        title: editForm.title,
      });
      if (error) {
        setEditError(error.message);
      } else {
        setIsEditing(false);
      }
    } catch {
      setEditError('تعذّر حفظ التعديلات. يرجى المحاولة لاحقاً.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarUploading(true);
    try {
      const { data: uploadData } = await mediaService.uploadAvatar(file);
      const url = uploadData?.url || uploadData;
      await updateProfile({ avatarUrl: url });
    } catch {
      // Silently handle avatar upload failure - the user will see the old avatar
    } finally {
      setAvatarUploading(false);
      // Reset file input so the same file can be re-selected
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const isLoading = activeTab === 'published' ? publishedLoading : bookmarksLoading;
  const error = activeTab === 'published' ? publishedError : bookmarksError;
  const rawArticles = activeTab === 'published' ? publishedArticles : bookmarkArticles;
  const displayedArticles = rawArticles.map((a) => mapArticle(a));

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <Header />

      <main className="pt-16 lg:pt-20">
        {/* Profile Header */}
        <section className="py-12 md:py-16 lg:py-20 border-b border-[var(--color-border)]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
            {/* Large Circular Avatar */}
            <div className="flex justify-center mb-6">
              <div
                className="relative w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden shadow-warm-lg cursor-pointer group"
                onClick={handleAvatarClick}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="صورة المستخدم"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[var(--color-primary)]/20 to-[var(--color-primary)]/5 flex items-center justify-center">
                    <Icon name="User" size={64} color="var(--color-primary)" />
                  </div>
                )}
                {/* Avatar upload overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  {avatarUploading ? (
                    <Icon name="Loader" size={28} color="white" className="animate-spin" />
                  ) : (
                    <Icon name="Camera" size={28} color="white" />
                  )}
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>
            </div>

            {/* User Name & Bio - Editable */}
            {isEditing ? (
              <div className="max-w-md mx-auto mb-6 space-y-4">
                <input
                  type="text"
                  value={editForm.fullName}
                  onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))}
                  placeholder="الاسم الكامل"
                  className="w-full text-center text-2xl md:text-3xl font-bold bg-transparent border-b-2 border-[var(--color-border)] focus:border-[var(--color-primary)] outline-none text-[var(--color-foreground)] py-2"
                  style={{ fontFamily: 'Aref Ruqaa, serif' }}
                  dir="rtl"
                />
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="اللقب (مثال: كاتب، مفكر)"
                  className="w-full text-center text-base bg-transparent border-b border-[var(--color-border)] focus:border-[var(--color-primary)] outline-none text-[var(--color-muted-foreground)] py-2"
                  style={{ fontFamily: 'Amiri, serif' }}
                  dir="rtl"
                />
                <textarea
                  value={editForm.bio}
                  onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))}
                  placeholder="النبذة التعريفية"
                  rows={3}
                  className="w-full text-center text-base bg-transparent border border-[var(--color-border)] focus:border-[var(--color-primary)] outline-none text-[var(--color-muted-foreground)] py-2 px-3 rounded-lg resize-none"
                  style={{ fontFamily: 'Amiri, serif', lineHeight: '1.8' }}
                  dir="rtl"
                />
                {editError && (
                  <p className="text-sm text-[var(--color-error)]" style={{ fontFamily: 'Amiri, serif' }}>
                    {editError}
                  </p>
                )}
                <div className="flex items-center justify-center gap-3">
                  <Button
                    onClick={handleSaveEdit}
                    disabled={editSaving || profileLoading}
                    className="rounded-full px-6 py-2"
                    style={{
                      fontFamily: 'Amiri, serif',
                      backgroundColor: 'var(--color-primary)',
                      color: 'white',
                      opacity: (editSaving || profileLoading) ? 0.6 : 1,
                    }}
                  >
                    {editSaving ? 'جارٍ الحفظ...' : 'حفظ'}
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    disabled={editSaving}
                    className="rounded-full px-6 py-2"
                    style={{
                      fontFamily: 'Amiri, serif',
                      backgroundColor: 'transparent',
                      color: 'var(--color-muted-foreground)',
                      border: '1px solid var(--color-border)',
                    }}
                  >
                    إلغاء
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* User Name */}
                <h1
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-1"
                  style={{ fontFamily: 'Aref Ruqaa, serif' }}
                >
                  {displayName}
                </h1>

                {/* Title */}
                {userProfile?.title && (
                  <p
                    className="text-sm md:text-base text-[var(--color-primary)] mb-3"
                    style={{ fontFamily: 'Amiri, serif' }}
                  >
                    {userProfile.title}
                  </p>
                )}

                {/* Bio */}
                <p
                  className="text-base md:text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto mb-4"
                  style={{ fontFamily: 'Amiri, serif', lineHeight: '1.8' }}
                >
                  {userProfile?.bio || 'كاتب ومفكر، أشارك أفكاري وتأملاتي في عالم الأدب والفلسفة'}
                </p>

                {/* Edit Profile Button */}
                <button
                  onClick={handleStartEdit}
                  className="inline-flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-primary)] transition-colors mb-6"
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  <Icon name="Edit2" size={16} />
                  تعديل الملف الشخصي
                </button>
              </>
            )}

            {/* Stats */}
            <div className="flex items-center justify-center gap-8 text-sm md:text-base">
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(articleCount)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  منشورات
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(totalClaps)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  إعجاب
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(followerCount)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  متابعين
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-2xl font-bold text-[var(--color-foreground)]" style={{ fontFamily: 'Aref Ruqaa, serif' }}>
                  {toArabicDigits(circlesCount)}
                </span>
                <span className="text-[var(--color-muted-foreground)]" style={{ fontFamily: 'Amiri, serif' }}>
                  دوائر
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs Navigation */}
        <section className="border-b border-[var(--color-border)]">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-8">
              <button
                onClick={() => setActiveTab('published')}
                className={`py-4 px-6 text-base md:text-lg font-medium transition-colors relative ${
                  activeTab === 'published' ?'text-[var(--color-primary)]' :'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
                style={{ fontFamily: 'Amiri, serif' }}
              >
                منشورات
                {activeTab === 'published' && (
                  <span className="absolute bottom-0 right-0 w-full h-0.5 bg-[var(--color-primary)]" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('bookmarks')}
                className={`py-4 px-6 text-base md:text-lg font-medium transition-colors relative ${
                  activeTab === 'bookmarks' ?'text-[var(--color-primary)]' :'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]'
                }`}
                style={{ fontFamily: 'Amiri, serif' }}
              >
                محفوظات
                {activeTab === 'bookmarks' && (
                  <span className="absolute bottom-0 right-0 w-full h-0.5 bg-[var(--color-primary)]" />
                )}
              </button>
            </div>
          </div>
        </section>

        {/* Content Grid */}
        <section className="py-12 md:py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
            {isLoading ? (
              /* Loading skeleton */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="w-full aspect-square bg-[var(--color-border)] rounded-full mx-auto mb-6" style={{ maxWidth: '320px' }} />
                    <div className="space-y-3 text-center px-6">
                      <div className="h-6 bg-[var(--color-border)] rounded w-3/4 mx-auto" />
                      <div className="h-4 bg-[var(--color-border)] rounded w-full mx-auto" />
                      <div className="h-4 bg-[var(--color-border)] rounded w-5/6 mx-auto" />
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              /* Error state */
              <div className="text-center py-16">
                <Icon
                  name="AlertCircle"
                  size={64}
                  color="var(--color-error)"
                  className="mx-auto mb-4"
                />
                <p
                  className="text-base text-[var(--color-error)] mb-6"
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  {error}
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  className="rounded-full px-8 py-3"
                  style={{
                    fontFamily: 'Amiri, serif',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white'
                  }}
                >
                  إعادة المحاولة
                </Button>
              </div>
            ) : displayedArticles?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {displayedArticles.map((article) => (
                  <ArticleCard
                    key={article?.id}
                    article={article}
                    isDiscussion={article?.type === 'Discussion'}
                    isBookmarked={bookmarkedIds.has(article?.id)}
                    onBookmarkToggle={handleBookmarkToggle}
                    isAuthenticated={isAuthenticated}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Icon
                  name={activeTab === 'published' ? 'FileText' : 'Bookmark'}
                  size={64}
                  color="var(--color-muted-foreground)"
                  className="mx-auto mb-4"
                />
                <h3
                  className="text-xl md:text-2xl font-bold text-[var(--color-foreground)] mb-2"
                  style={{ fontFamily: 'Aref Ruqaa, serif' }}
                >
                  {activeTab === 'published' ? 'لا توجد منشورات بعد' : 'لا توجد محفوظات'}
                </h3>
                <p
                  className="text-base text-[var(--color-muted-foreground)] mb-6"
                  style={{ fontFamily: 'Amiri, serif' }}
                >
                  {activeTab === 'published' ? 'ابدأ بكتابة أول قصة لك' : 'احفظ المقالات للعودة إليها لاحقاً'}
                </p>
                <Button
                  onClick={() => navigate('/creator-studio')}
                  className="rounded-full px-8 py-3"
                  style={{
                    fontFamily: 'Amiri, serif',
                    backgroundColor: 'var(--color-primary)',
                    color: 'white'
                  }}
                >
                  أضِف أثرك
                </Button>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default UserProfile;
